import { z } from "zod";

import type { JsonObject } from "../../types";

import { coerceToJsonObject } from "../../json";
import { jsonValueSchema } from "../../schemas/jsonValue";
import { implementationContextSchema } from "../implementationContext";
import {
  commitStatusSchema,
  executionReviewFeedbackEntrySchema,
} from "../schemas";
import { executionNodeContextArtifactsSchema } from "./executionArtifacts";

const phaseBudgetOverrideMapSchema = z.record(
  z.string(),
  z.number().int().positive().nullable(),
);

const phaseBudgetOverridesByModeSchema = z.record(
  z.string(),
  phaseBudgetOverrideMapSchema,
);

const executionCompletionFileStatusValues = [
  "created",
  "deleted",
  "modified",
  "renamed",
] as const;

const executionSetupStringMapSchema = z.record(
  z.string().trim().min(1),
  z.string().trim().min(1),
);

export const executionSetupDispatchEnvelopeSchema = z
  .object({
    branch_name: z.string().trim().min(1).optional(),
    execution_request_id: z.string().trim().min(1),
    extensive_plan_phase_trace_id: z.string().trim().min(1).nullable(),
    issue_key: z.string().trim().min(1),
    primary_repository_slug: z.string().trim().min(1).nullable(),
    provider_id: z.string().trim().min(1).nullable(),
    revision_id: z.string().trim().min(1),
    selected_base_shas: executionSetupStringMapSchema,
    selected_branches: executionSetupStringMapSchema,
  })
  .strict();

export type ExecutionSetupDispatchEnvelope = z.infer<
  typeof executionSetupDispatchEnvelopeSchema
>;

export const executionSetupCompletionParsedResultSchema = z
  .object({
    branches: z.array(z.string().trim().min(1)),
    summary: z.string().trim().min(1),
  })
  .strict();

export const executionSetupCompletionEnvelopeSchema = z
  .object({
    execution_request_id: z.string().trim().min(1),
    parsed_result: executionSetupCompletionParsedResultSchema,
  })
  .strict();

export type ExecutionSetupCompletionEnvelope = z.infer<
  typeof executionSetupCompletionEnvelopeSchema
>;

export const executionDispatchEnvelopeSchema = z
  .object({
    branch_name: z.string().trim().min(1),
    execution_node_id: z.string().trim().min(1),
    execution_request_id: z.string().trim().min(1),
    files: z.array(z.string().trim().min(1)),
    implementation_context: implementationContextSchema,
    phase_budget_overrides: phaseBudgetOverrideMapSchema.optional(),
    phase_budget_overrides_by_mode: phaseBudgetOverridesByModeSchema.optional(),
    plan_item_id: z.string().trim().min(1),
    repository_slug: z.string().trim().min(1),
    tests: z.array(z.string().trim().min(1)),
    ticket_id: z.string().trim().min(1),
  })
  .strict();

export type ExecutionDispatchEnvelope = z.infer<
  typeof executionDispatchEnvelopeSchema
>;

export const executionCompletionCommitSchema = z
  .object({
    commit_message: z.string().trim().min(1).optional(),
    commit_sha: z.string().nullable(),
    commit_url: z.string().nullable().optional(),
    conflict_files: z.array(z.string().trim().min(1)).optional(),
    message: z.string().optional(),
    status: commitStatusSchema,
    workspace_dirty: z.boolean().optional(),
  })
  .strict();

export type ExecutionCompletionCommit = z.infer<
  typeof executionCompletionCommitSchema
>;

export const executionCompletionFileChangeSchema = z
  .object({
    added_lines: z.number().nullable(),
    path: z.string().trim().min(1),
    removed_lines: z.number().nullable(),
    status: z.enum(executionCompletionFileStatusValues),
    ui_ux_visual_change: z
      .object({
        assets: z.array(z.string().trim().min(1)).optional(),
        notes: z.string().optional(),
        summary: z.string().trim().min(1),
      })
      .nullable(),
  })
  .strict();

export const executionCompletionParsedResultSchema = z
  .object({
    context_artifacts: executionNodeContextArtifactsSchema.optional(),
    files_changed: z.array(executionCompletionFileChangeSchema),
    functional_status: z.string().nullable(),
    review_feedback: z.array(executionReviewFeedbackEntrySchema),
    review_status: z.string().trim().min(1),
    summary: z.string(),
  })
  .strict();

export const executionCompletionEnvelopeSchema = z
  .object({
    commit: executionCompletionCommitSchema.optional(),
    parsed_result: executionCompletionParsedResultSchema,
  })
  .strict();

export type ExecutionCompletionEnvelope = z.infer<
  typeof executionCompletionEnvelopeSchema
>;

export const executionImplementationResultEnvelopeSchema = z
  .object({
    commit: executionCompletionCommitSchema.optional(),
    finalText: z.string().optional(),
    message: z.string().optional(),
    metadata: z.record(z.string(), jsonValueSchema).optional(),
    parsed_result: executionCompletionParsedResultSchema,
    parsed_result_errors: jsonValueSchema.optional(),
    parsed_result_source: jsonValueSchema.optional(),
    summary: z.string().optional(),
  })
  .strict();

export type ExecutionImplementationResultEnvelope = z.infer<
  typeof executionImplementationResultEnvelopeSchema
>;

export function parseExecutionDispatchEnvelope(
  value: unknown,
): ExecutionDispatchEnvelope | null {
  const parsed = executionDispatchEnvelopeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function parseExecutionSetupDispatchEnvelope(
  value: unknown,
): ExecutionSetupDispatchEnvelope | null {
  const parsed = executionSetupDispatchEnvelopeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function serializeExecutionDispatchEnvelope(
  envelope: ExecutionDispatchEnvelope,
): JsonObject {
  return coerceToJsonObject(executionDispatchEnvelopeSchema.parse(envelope));
}

export function serializeExecutionSetupDispatchEnvelope(
  envelope: ExecutionSetupDispatchEnvelope,
): JsonObject {
  return coerceToJsonObject(
    executionSetupDispatchEnvelopeSchema.parse(envelope),
  );
}

export function parseExecutionSetupCompletionEnvelope(
  value: unknown,
): ExecutionSetupCompletionEnvelope | null {
  const parsed = executionSetupCompletionEnvelopeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function serializeExecutionCompletionEnvelope(
  envelope: ExecutionCompletionEnvelope,
): JsonObject {
  return coerceToJsonObject(executionCompletionEnvelopeSchema.parse(envelope));
}

export function serializeExecutionSetupCompletionEnvelope(
  envelope: ExecutionSetupCompletionEnvelope,
): JsonObject {
  return coerceToJsonObject(
    executionSetupCompletionEnvelopeSchema.parse(envelope),
  );
}

export function parseExecutionImplementationResultEnvelope(
  value: unknown,
): ExecutionImplementationResultEnvelope | null {
  const parsed = executionImplementationResultEnvelopeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function serializeExecutionImplementationResultEnvelope(
  envelope: ExecutionImplementationResultEnvelope,
): JsonObject {
  return coerceToJsonObject(
    executionImplementationResultEnvelopeSchema.parse(envelope),
  );
}
